import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Shirt, Film, Music, Newspaper, Plane, Heart } from 'lucide-react';

const categories = [
  { name: "fashion", icon: Shirt },
  { name: "film", icon: Film },
  { name: "music", icon: Music },
  { name: "media", icon: Newspaper },
  { name: "travel", icon: Plane },
  { name: "wellness", icon: Heart },
];

const skills = [
  "UI Design",
  "Mobile/Web App Development",
  "Product Innovation Strategy",
  "User Research",
  "GTM Strategy",
  "Phygital UX Strategy",
  "Tools + Operations for Innovators",
  "Wellness as Infrastructure",
  "Invisible Disabilities",
  "Human-Centered Design",
  "Energy-Aware Systems",
  "Accessibility Beyond Compliance",
  "Creative Marketing",
  "Content Creation",
  "Social Media Operations",
  "Personal Branding",
  "Growth Hacking",
  "Brand Identity Development"
];

const SkillPill = ({ skill, index }) => {
  const colors = [
    'bg-pink-100 text-pink-700 border-pink-200',
    'bg-blue-100 text-blue-700 border-blue-200',
    'bg-amber-100 text-amber-700 border-amber-200',
    'bg-purple-100 text-purple-700 border-purple-200',
    'bg-teal-100 text-teal-700 border-teal-200',
    'bg-cyan-100 text-cyan-700 border-cyan-200',
    'bg-rose-100 text-rose-700 border-rose-200',
    'bg-emerald-100 text-emerald-700 border-emerald-200',
  ];
  
  return (
    <span 
      className={`inline-block px-3 py-1.5 text-[10px] md:text-xs rounded-full border ${colors[index % colors.length]} font-medium transition-transform hover:scale-105`}
    >
      {skill}
    </span>
  );
};

const CategoryIcon = ({ category, index }) => {
  const Icon = category.icon;
  const colors = [
    'text-rose-500 bg-rose-50',
    'text-blue-500 bg-blue-50',
    'text-purple-500 bg-purple-50',
    'text-teal-500 bg-teal-50',
    'text-orange-500 bg-orange-50',
    'text-pink-500 bg-pink-50',
  ];
  
  return (
    <div className="flex flex-col items-center gap-1">
      <div 
        className={`p-2 md:p-3 rounded-xl ${colors[index]} transition-transform hover:scale-110`}
        aria-hidden="true"
      >
        <Icon className="w-5 h-5 md:w-6 md:h-6" />
      </div>
      <span className="text-[10px] md:text-xs text-stone-600 font-medium">{category.name}</span>
    </div>
  );
};

const WorkWithMe = () => {
  return (
    <section className="py-8 md:py-12 bg-white relative overflow-hidden" aria-labelledby="work-with-me-heading">
      <div className="container mx-auto px-4">
        <div className="flex flex-col md:flex-row gap-8 items-center max-w-5xl mx-auto">
          {/* GIF on left */}
          <div className="w-full md:w-1/3 flex justify-center">
            <div className="rounded-2xl overflow-hidden shadow-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_naenao-duality/artifacts/218jc5h3_working%20-%20tbn.gif"
                alt="Naomi working on a whiteboard, brainstorming ideas"
                className="w-full max-w-[250px] md:max-w-[280px] h-auto"
              />
            </div>
          </div>
          
          {/* Content on right */}
          <div className="w-full md:w-2/3">
            {/* Skills title */}
            <div className="text-center mb-4">
              <h2 id="work-with-me-heading" className="sr-only">Work With Me - Skills and Expertise</h2>
              <p className="text-sm md:text-base text-stone-700 font-medium mb-1">
                <span role="img" aria-label="brain">🧠</span> tabs I have open 24/7
              </p>
              <p className="text-[10px] md:text-xs text-stone-500">
                (basically my brain's browser history)
              </p>
            </div>
            
            {/* Skills cloud */}
            <ul className="flex flex-wrap justify-center gap-2 md:gap-3 mb-6 list-none" aria-label="Skills and expertise">
              {skills.map((skill, index) => (
                <li key={skill}>
                  <SkillPill skill={skill} index={index} />
                </li>
              ))}
            </ul>
            
            {/* Categories */}
            <div className="text-center mb-6">
              <p className="text-[10px] md:text-xs text-stone-500 mb-3">at the intersection of</p>
              <ul className="flex flex-wrap justify-center gap-4 md:gap-6 list-none" aria-label="Industry categories">
                {categories.map((cat, index) => (
                  <li key={cat.name}>
                    <CategoryIcon category={cat} index={index} />
                  </li>
                ))}
              </ul>
            </div>
            
            <div className="text-center">
              <a
                href="https://airtable.com/appsMr3rlg8bSkFk5/pagfPrUfQC0yzZTla/form"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Work with me - opens contact form in new tab"
              >
                <Button
                  size="lg"
                  className="group bg-stone-900 hover:bg-stone-800 text-white px-5 py-4 text-sm rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
                >
                  <span>work with me</span>
                  <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkWithMe;
